import React, { FC, useCallback, useMemo, useState } from "react";
import { Platform, TouchableOpacity } from "react-native";
import { Image, View, Box, Icon, COLOR } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Text } from "@lookiero/sty-psp-ui";
import {
  AspectRatioView,
  Bullets,
  Carousel,
  LazyImage,
  RenderBulletsFunction,
  RenderItemFunction,
  useMediaImage,
} from "@lookiero/sty-psp-ui";
import { TouchableWithoutFeedback } from "../../../../components/touchableWithoutFeedback/TouchableWithoutFeedback";
import { useExtendedScreen } from "../../../../hooks/useExtendedScreen";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { Media } from "../../../../shared/types";
import { style as productImageGalleryStyle, getImageWidthInPixels } from "./ProductImageGallery.styles";

const ITEM_PER_ROW = 2;
const ACTIVE_OPACITY = 0.6;

interface ProductImageGalleryProps {
  readonly images: Media[];
  readonly handleOnClickImage: (index: number) => void;
  readonly handleOnPressButton: () => void;
}

const ProductImageGallery: FC<ProductImageGalleryProps> = ({ images, handleOnClickImage, handleOnPressButton }) => {
  const benefitsMoreLooksButtonText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.BENEFITS_MORE_LOOKS_BUTTON,
  });
  const cdnImageUrl = useMediaImage();
  const screen = useExtendedScreen();

  const style = useMemo(() => productImageGalleryStyle(), []);

  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);

  const [activeIndex, setActiveIndex] = useState(0);
  const handleOnActiveChanged = useCallback((activeIndex: number) => {
    setActiveIndex(activeIndex);
  }, []);

  const numRows = images.length / ITEM_PER_ROW;
  const areEvenImages = Number.isInteger(numRows);

  const isItemInLastRow = (index: number) => {
    const itemRow = Math.floor(index / ITEM_PER_ROW);

    return itemRow >= Math.floor(numRows) - 1;
  };

  const handleOnLoadEnd = useCallback(() => {
    setIsImageLoaded(true);
  }, []);

  const handleOnPress = useCallback(() => {
    if (Platform.OS === "web") {
      handleOnClickImage(activeIndex);
    }
  }, [activeIndex, handleOnClickImage]);

  const renderItem: RenderItemFunction<Media> = useCallback(
    ({ item }) => (
      <AspectRatioView key={item.perspective} aspectRatio={4 / 3}>
        <TouchableWithoutFeedback style={style.imagePressable} onPress={handleOnPress}>
          <LazyImage
            hiResSrc={cdnImageUrl({ url: item.url, width: 600 })}
            resizeMode="cover"
            src={cdnImageUrl({ url: item.url, width: 600, dpi: 1 })}
            style={{ view: style.image }}
          />
        </TouchableWithoutFeedback>
      </AspectRatioView>
    ),
    [cdnImageUrl, handleOnPress, style.image, style.imagePressable],
  );

  const renderBullets: RenderBulletsFunction = useCallback(
    ({ activeIndex, count, onChange }) => <Bullets activeIndex={activeIndex} count={count} onChange={onChange} />,
    [],
  );

  return screen.XL ? (
    <View style={style.wrapper}>
      {images.map(({ url: imageUrl }, index) => {
        const imageWidthInPixels = getImageWidthInPixels(screen);
        const itemInLastRow = isItemInLastRow(index);

        return (
          <Box key={imageUrl} size={{ L: !areEvenImages && itemInLastRow ? "1/3" : "1/2" }} style={style.itemWrapper}>
            <View style={style.itemContent}>
              <TouchableWithoutFeedback
                style={style.imageCta}
                testID="product-preview-image-container"
                onPress={() => handleOnClickImage(index)}
              >
                <Image
                  resizeMode="cover"
                  src={cdnImageUrl({ url: imageUrl, width: imageWidthInPixels })}
                  style={[style.image, isImageLoaded ? style.imageLoaded : style.imageNotLoaded]}
                  onLoadEnd={handleOnLoadEnd}
                />
              </TouchableWithoutFeedback>
            </View>
          </Box>
        );
      })}
    </View>
  ) : (
    <View style={style.sliderWrapper}>
      <Carousel
        activeIndex={activeIndex}
        bullets={renderBullets}
        data={images}
        onActiveIndexChanged={handleOnActiveChanged}
      >
        {renderItem}
      </Carousel>

      <TouchableOpacity activeOpacity={ACTIVE_OPACITY} style={style.button} onPress={handleOnPressButton}>
        <Text level={2} style={style.buttonText} variant="detail">
          {benefitsMoreLooksButtonText}
        </Text>

        <Icon color={COLOR.BG_BASE} name="arrow_down_small" />
      </TouchableOpacity>
    </View>
  );
};

export { ProductImageGallery };
